import {
  AdminInvoiceReportCancelStatus,
  useCancelAdminInvoiceReportService,
} from '@/context/services/admin_invoice_reports/AdminInvoiceReportCancel.service';
import { useInvalidateAdminInvoiceReportDrawer } from '@/context/services/admin_invoice_reports/AdminInvoiceReportDrawer.service';
import { useCheckInvoiceReportsProtectedResources } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportsProtectedResources';
import { useTranslation } from '@/i18n';
import { toHtmlNode } from '@/utils/toHtmlNode';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback } from 'react';
import { useTriggerFindTargetReport } from '../../List/context/TriggerFindTargetReportContext';

type UseCancelAdminInvoiceReportProps = {
  refetch: () => void | Promise<unknown>;
  onDrawerClose: () => void;
  selectedId: string | null;
};

const TOAST_DURATION = 5;

export const useCancelAdminInvoiceReport = ({
  refetch,
  onDrawerClose,
  selectedId,
}: UseCancelAdminInvoiceReportProps) => {
  const { onTriggerBackToPageOneIfNoData } = useTriggerFindTargetReport();
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { mutateAsync: cancelAdminInvoiceReport } =
    useCancelAdminInvoiceReportService();
  const { handleCheckPermission } = useCheckInvoiceReportsProtectedResources();
  const { invalidateDrawerIfOpen } =
    useInvalidateAdminInvoiceReportDrawer(selectedId);

  const cancelReport = useCallback(
    (reportId: string) => async () => {
      const result = await cancelAdminInvoiceReport(reportId);

      if (result.status === AdminInvoiceReportCancelStatus.SUCCESS) {
        notify.success({
          title: t('invoice_reports.action_menu.cancel_success_message'),
          duration: TOAST_DURATION,
        });
        invalidateDrawerIfOpen(reportId);
        await refetch?.();
        onTriggerBackToPageOneIfNoData();
        return;
      }

      if (result.status === AdminInvoiceReportCancelStatus.UNAUTHORIZED) {
        location.reload();
        return;
      }

      if (result.status === AdminInvoiceReportCancelStatus.NOT_FOUND) {
        if (reportId === selectedId) {
          onDrawerClose();
        }
        notify.error({
          title: toHtmlNode(result.message ?? t('system_error')),
          duration: TOAST_DURATION,
        });
        await refetch?.();
        onTriggerBackToPageOneIfNoData();
        return;
      }

      const errorMessage = result.message ?? t('system_error');
      await handleCheckPermission({
        fnCheckPermission: refetch,
        onCheckSuccess: () => {
          invalidateDrawerIfOpen(reportId);
          notify.error({
            title: toHtmlNode(errorMessage),
            duration: TOAST_DURATION,
          });
          onTriggerBackToPageOneIfNoData();
        },
        onCheckFailure: () => {
          location.reload();
        },
      });
    },
    [
      cancelAdminInvoiceReport,
      handleCheckPermission,
      invalidateDrawerIfOpen,
      notify,
      onDrawerClose,
      onTriggerBackToPageOneIfNoData,
      refetch,
      selectedId,
      t,
    ]
  );

  return { cancelReport };
};
